import { Calculator, Database, Plus, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CSV_TEMPLATE_EXAMPLE_COUNT } from '@/lib/csv'

// The "get your people in" choices, shared by DataInput's empty state and the
// first-run onboarding flow. File handling stays with the owner: this renders
// the hero drop zone from the drag props and callbacks it is given.
export default function CensusStartOptions({
  dropZoneProps,
  isDragging = false,
  isUploading = false,
  onDownloadTemplate,
  onLoadExamples,
  onAddBlankRow,
  onOpenEstimator,
  heading = 'Add your employee list',
}) {
  const heroStateClass = isDragging
    ? 'border-primary bg-primary/5 text-primary'
    : 'border-primary/40 bg-card/80 hover:border-primary hover:bg-accent/50'

  return (
    <div className="space-y-4">
      <label
        {...dropZoneProps}
        className={[
          'flex cursor-pointer flex-col items-center justify-center gap-2.5 rounded-lg border-2 border-dashed px-6 py-14 text-center transition-colors',
          heroStateClass,
        ].join(' ')}
      >
        <Upload className="h-8 w-8 text-muted-foreground" />
        <span className="text-lg font-semibold text-foreground">
          {isUploading ? 'Uploading…' : isDragging ? 'Drop your files here' : heading}
        </span>
        <span className="text-sm text-muted-foreground">
          Drop a CSV or Excel file here, or click to browse
        </span>
        <span className="text-xs text-muted-foreground">
          DOB and compensation are required · ID is auto-generated if blank
        </span>
      </label>

      <div className="flex justify-center">
        <button
          type="button"
          onClick={onDownloadTemplate}
          className="text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground"
        >
          Need a starting file? Download the CSV template ({CSV_TEMPLATE_EXAMPLE_COUNT} example employees)
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2">
        <span className="text-xs text-muted-foreground">or</span>
        <Button type="button" variant="outline" size="sm" className="h-8" onClick={onLoadExamples}>
          <Database className="mr-1.5 h-3.5 w-3.5" />
          Load example data
        </Button>
        <Button type="button" variant="outline" size="sm" className="h-8" onClick={onAddBlankRow}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add blank row
        </Button>
      </div>

      <div className="flex justify-center">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="h-auto flex-col items-start gap-0.5 px-4 py-2.5 text-left sm:flex-row sm:items-center sm:gap-2"
          title="Estimate a plan without an employee file"
          onClick={onOpenEstimator}
        >
          <span className="flex items-center gap-1.5 text-sm font-medium">
            <Calculator className="h-3.5 w-3.5" />
            No employee list yet? Estimate from rough numbers
          </span>
          <span className="text-xs font-normal text-muted-foreground">
            Builds a sample employee list you can replace later.
          </span>
        </Button>
      </div>
    </div>
  )
}
